// BookingSuccessModal — shown after a successful POST /api/v1/bookings.
//
// The brief specifies three actions:
//   1. Book Another  — clears the form (caller decides how)
//   2. View in Diary — navigate to /diary/results encrypted with
//                      driver_id + week_start
//   3. View Booking  — navigate to /bookings/:enc_id
//
// Encrypting the diary URL params here keeps the modal cohesive — the
// caller doesn't need to know the encryptParams convention.

import { useState } from 'react';
import { CheckCircle2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Modal } from '../../../components/ui/Modal/Modal';
import { Button } from '../../../components/ui/Button/Button';
import { encryptParams, UrlParamError } from '../../../utils/urlParams';
import { useAuthStore } from '../../../store/authStore';
import { useToast } from '../../../hooks/useToast';
import type { BookingSummary, CreateBookingResponse } from '../../../types/api';
import styles from './BookingSuccessModal.module.css';

export interface BookingSuccessModalProps {
  open: boolean;
  result: CreateBookingResponse | null;
  driverName: string | null;
  /** Total vehicle count from the master + related rows. */
  vehicleCount: number;
  onClose: () => void;
  onBookAnother: () => void;
}

function mondayOfISO(dateIso: string): string {
  // dateIso = YYYY-MM-DD; Monday-snapping logic mirrors DiaryPage.mondayOfISO.
  const d = new Date(`${dateIso}T00:00:00`);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

function formatDate(dateIso: string): string {
  const d = new Date(`${dateIso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return dateIso;
  return d.toLocaleDateString(undefined, {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export function BookingSuccessModal({
  open,
  result,
  driverName,
  vehicleCount,
  onClose,
  onBookAnother,
}: BookingSuccessModalProps): JSX.Element | null {
  const navigate = useNavigate();
  const toast = useToast();
  const urlParamSecret = useAuthStore((s) => s.urlParamSecret);
  const [navigating, setNavigating] = useState(false);

  if (!open || !result) return null;

  const master: BookingSummary = result.master;
  const dateLabel = formatDate(master.date);

  async function goToDiary() {
    if (!result || !master.driver_id) {
      toast.error('No driver assigned — cannot open diary.');
      return;
    }
    if (!urlParamSecret) {
      toast.error('Session not ready. Please refresh.');
      return;
    }
    setNavigating(true);
    try {
      const d = await encryptParams(
        { driver_id: master.driver_id, week_start: mondayOfISO(master.date) },
        urlParamSecret,
      );
      navigate(`/collections/diary/results?d=${encodeURIComponent(d)}`);
    } catch (err) {
      const message =
        err instanceof UrlParamError
          ? err.message
          : 'Could not build diary link.';
      toast.error(message);
      setNavigating(false);
    }
  }

  function goToBooking() {
    if (!result) return;
    navigate(`/collections/bookings/${encodeURIComponent(master.enc_id)}`);
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      ariaLabel="Booking confirmed"
      size="md"
      preventClose={navigating}
      footer={
        <>
          <Button variant="secondary" onClick={onBookAnother} disabled={navigating}>
            Book Another
          </Button>
          <Button variant="primary" onClick={goToDiary} loading={navigating}>
            View in Diary
          </Button>
          <Button variant="primary" onClick={goToBooking} disabled={navigating}>
            View Booking
          </Button>
        </>
      }
    >
      <div className={styles.hero}>
        <div className={styles.iconRing}>
          <CheckCircle2 size={36} aria-hidden />
        </div>
        <h2 className={styles.title}>Booking Confirmed</h2>
        <span className={styles.reference} data-testid="booking-reference">
          {result.reference}
        </span>
      </div>

      <dl className={styles.grid}>
        <div className={styles.row}>
          <dt>Customer</dt>
          <dd>{master.name}</dd>
        </div>
        <div className={styles.row}>
          <dt>Driver</dt>
          <dd>{driverName ?? '—'}</dd>
        </div>
        <div className={styles.row}>
          <dt>Date</dt>
          <dd>
            {dateLabel} · {master.timeframe}
          </dd>
        </div>
        <div className={styles.row}>
          <dt>Vehicles</dt>
          <dd>{vehicleCount}</dd>
        </div>
      </dl>
    </Modal>
  );
}
